//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect } from 'react';
import { useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import { Grid, Container } from '@material-ui/core';
import SimpleBar from 'simplebar-react';
import { useParams } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { logEvent, logPageView } from '../system/firebase';
import { LayoutContext, AppContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import useSimpleBarRef from '../common/hooks/useSimpleBarRef';
import { ANALYTICS_EVENT_SURVEY_SUBMITTED, PAGE_TITLE_SURVEY, USER_TYPE_EMP, USER_TYPE_JS } from '../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { saveSurvey, fetchSurveyDataById } from '../common/services/customerSupportServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../common/elemental/AfitdPageHead';
// import AfitdTypography from '../../common/elemental/AfitdTypography';
import TopNav from '../topNav/TopNav';
import PageMessage from '../common/elemental/PageMessage';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// EMployers Steps
import EmpStep1Page from './subComponents/employer/EmpStep1Page';
import EmpStep2Page from './subComponents/employer/EmpStep2Page';
import EmpStep3Page from './subComponents/employer/EmpStep3Page';
import EmpStep4Page from './subComponents/employer/EmpStep4Page';
import EmpStep5Page from './subComponents/employer/EmpStep5Page';
import EmpStep6Page from './subComponents/employer/EmpStep6Page';
import EmpStep7Page from './subComponents/employer/EmpStep7Page';
// Job Seeker Steps
import JSStep1Page from './subComponents/jobSeeker/JSStep1Page';
import JSStep2Page from './subComponents/jobSeeker/JSStep2Page';
import JSStep3Page from './subComponents/jobSeeker/JSStep3Page';
import JSStep4Page from './subComponents/jobSeeker/JSStep4Page';
import JSStep5Page from './subComponents/jobSeeker/JSStep5Page';
import JSStep6Page from './subComponents/jobSeeker/JSStep6Page';

import Footer from '../home/subComponents/Footer';
import SurveySuccess from './subComponents/SurveySuccess';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import { isBefore } from 'date-fns';
import _get from 'lodash/get';
import _find from 'lodash/find';
import _pick from 'lodash/pick';
import _filter from 'lodash/filter';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {},
    pageContainer: { backgroundColor: '#FAFAFC', overflow: 'hidden', height: '100%', minHeight: '100%', flexWrap: 'unset', overflowX: 'hidden' },
    pageTopNav: { maxHeight: '81px' },
    pageContent: { flex: 1, justifyContent: 'center' },
    nextButton: {
        textAlign: 'right',
        marginTop: '18px',
    },
    backButton: {
        textAlign: 'left',
        marginTop: '18px',
    },
    mainGridContainer: {
        // border: '1px solid red',
        // width: '100%',
        // // height: '640px',
        // backgroundColor: 'white',
        // borderRadius: '10px',
        // padding: '48px 112px 48px 112px',
        // [theme.breakpoints.down('sm')]: { padding: '44px 0px' },
    },
    container: {
        marginTop: 30,
        flex: 1,
        overflowY: 'hidden',
        width: '100%',
        // height: '640px',
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '48px 112px 48px 112px',
        [theme.breakpoints.down('md')]: { padding: '48px ' },
        [theme.breakpoints.down('sm')]: { padding: '0px 0px' },
    },
    progressGrid: {
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: { justifyContent: 'center', margin: '0 auto', width: '100%' },
    },
    gridBodyFooter: {
        marginBottom: '100px',
        justifyContent: 'space-between',
    },
    tempContainer: {
        width: '100%',
        [theme.breakpoints.down('sm')]: { padding: '20px' },
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes } = params;

    const defns = {
        pageContainer: { container: true, direction: 'column' },
        pageTopNav: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        pageContent: { item: true, container: true },
        mainGridContainer: {
            container: true,
        },
        tempContainer: { item: true },
        footerGridItem: {
            xs: 4,
        },
        progress: {
            variant: 'determinate',
            value: 66.66,
        },
        gridBodyFooter: {
            container: true,
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        progressGrid: { item: true, md: 2, sm: 6, xs: 6 },
        nextButton: { color: 'primary' },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const DEFAULT_SURVEY_USER = {
    companyName: null,
    firstName: null,
    lastName: null,
    emailAddress: null,
    phone: null,
    country: 'United States',
    state: null,
    userType: USER_TYPE_EMP,
};

const getSteps = () => {
    return ['step1', 'step2', 'step3', 'step4', 'step5', 'step6'];
};
const getEmpSteps = () => {
    return ['step1', 'step2', 'step3', 'step4', 'step5', 'step6', 'step7'];
};

const getEmpPageComponent = ({ pageId }) => {
    switch (pageId) {
        case 1:
            return <EmpStep1Page />;
        case 2:
            return <EmpStep2Page />;
        case 3:
            return <EmpStep3Page />;
        case 4:
            return <EmpStep4Page />;
        case 5:
            return <EmpStep5Page />;
        case 6:
            return <EmpStep6Page />;
        case 7:
            return <EmpStep7Page />;
        case 8:
            return <SurveySuccess />;

        default:
            return <EmpStep1Page />;
    }
};

const getJSPageComponent = ({ pageId }) => {
    switch (pageId) {
        case 1:
            return <JSStep1Page />;
        case 2:
            return <JSStep2Page />;
        case 3:
            return <JSStep3Page />;
        case 4:
            return <JSStep4Page />;
        case 5:
            return <JSStep5Page />;
        case 6:
            return <JSStep6Page />;
        case 7:
            return <SurveySuccess />;

        default:
            return <JSStep1Page />;
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const SurveyLayout = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const { surveyDocId } = useParams();

    const { userId, lovs } = useContext(AppContext);

    const context = useContext(LayoutContext);

    const { setSnackbarMessage, setSnackbarErrorMessage } = context;
    // const { pageId } = useParams();
    const { simpleBarRef: pageContentSimpleBarEl, scrollToTop: scrollToPageTop } = useSimpleBarRef();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { isShowJobs } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isLoadingSurvey, setIsLoadingSurvey] = useState(true);
    const [validSurveyCode, setValidSurveyCode] = useState(false);
    const [surveyCompleted, setSurveyCompleted] = useState(false);
    const [stepDirtyList, setStepDirtyList] = useState({});

    const [surveyUser, setSurveyUser] = useState(DEFAULT_SURVEY_USER);
    const [userType, setUserType] = useState(USER_TYPE_EMP);

    //? Employer Steps States
    const [empData, setEmpData] = useState({
        companyName: '',
        firstName: '',
        lastName: '',
        emailAddress: '',
        phone: '',
        country: '',
        state: '',
    });
    const [empHelpfulRating, setEmpHelpfulRating] = useState(0);
    const [empCommentMoreHelpful, setEmpCommentMoreHelpful] = useState('');
    const [continueUseRating, setContinueUseRating] = useState(0);
    const [commentNotContinueUse, setCommentNotContinueUse] = useState('');
    const [commentMostValue, setCommentMostValue] = useState('');
    const [commentImprove, setCommentImprove] = useState('');
    const [recommendRating, setRecommendRating] = useState(0);
    const [commentNotRecommend, setCommentNotRecommend] = useState('');
    const [cusSupportRating, setCusSupportRating] = useState(0);
    const [commentNotSatisfiedCusSupport, setCommentNotSatisfiedCusSupport] = useState('');

    //? Job Seeker Steps States
    const [jsData, setJsData] = useState({
        firstName: '',
        lastName: '',
        emailAddress: '',
        phone: '',
        country: 'United States',
        state: '',
    });
    const [jsHelpfulRating, setJsHelpfulRating] = useState(0);
    const [jsCommentMoreHelpful, setJsCommentMoreHelpful] = useState('');

    const [pageId, setPageID] = useState(1);
    const [countryCode, setCountryCode] = useState('');

    const totalSteps = userType === USER_TYPE_EMP ? getEmpSteps().length : getSteps().length;

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const empCountry = _get(surveyUser, 'country', '');
    const defaultCountryOption = _find(_get(lovs, 'countryLov'), o => o.name === empCountry || o.name === surveyUser?.country);
    const userCountryCode = _get(defaultCountryOption, 'iso2');
    const companyNameExists = !!surveyUser.companyName;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_SURVEY, { user_type: userType });
    }, [userType]);

    useEffect(() => {
        const fetchSurveyData = async () => {
            // if (surveyDocId !== '') getUserTypeFromSurvey({ surveyDocId, setUserType });
            setIsLoadingSurvey(true);
            const surveyData = await fetchSurveyDataById({ surveyDocId });

            if (surveyData) {
                const userType = surveyData.userType;
                const updateDt = surveyData.updateDt;
                const emailDt = surveyData.emailDt;

                const fieldNameList = ['companyName', 'firstName', 'lastName', 'email', 'country', 'state', 'userType'];
                const defaultNonNullFieldNameList = _filter(fieldNameList, name => surveyData[name]);

                setSurveyUser(prev => {
                    let nonNullSurveyData = _pick(surveyData, defaultNonNullFieldNameList);
                    return { ...prev, ...nonNullSurveyData };
                });

                setUserType(userType);

                if (updateDt) {
                    // survey has been submitted
                    setSurveyCompleted(true);
                } else if (isBefore(new Date(), emailDt)) {
                    // survey is valid
                    setValidSurveyCode(true);
                }
            } else {
                // survey code is invalid or survey link is more than 90 days old (today is after the emailDt - an email with a new link has been sent)
                setValidSurveyCode(false);
            }

            setIsLoadingSurvey(false);
        };

        fetchSurveyData();
    }, [surveyDocId]);

    useEffect(() => {
        const userType = surveyUser.userType;

        if (surveyUser) {
            if (userType === USER_TYPE_EMP) {
                setEmpData({
                    companyName: surveyUser.companyName,
                    firstName: surveyUser.firstName,
                    lastName: surveyUser.lastName,
                    emailAddress: surveyUser.email,
                    phone: '',
                    country: surveyUser.country,
                    state: surveyUser.state,
                });
            } else if (userType === USER_TYPE_JS) {
                setJsData({
                    firstName: surveyUser.firstName,
                    lastName: surveyUser.lastName,
                    emailAddress: surveyUser.email,
                    phone: '',
                    country: surveyUser.country,
                    state: surveyUser.state,
                });
            }
            setCountryCode(userCountryCode);
        }
    }, [surveyUser, userCountryCode]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleSubmitSurvey = async () => {
        let data = {};
        if (userType === USER_TYPE_EMP) {
            data = {
                ...empData,
                commentMoreHelpful: empCommentMoreHelpful,
                helpful: empHelpfulRating,
            };
        }
        if (userType === USER_TYPE_JS) {
            data = {
                ...jsData,
                commentMoreHelpful: jsCommentMoreHelpful,
                helpful: jsHelpfulRating,
            };
        }
        data = {
            ...data,
            commentImprove,
            commentMostValue,
            commentNotContinueUse,
            commentNotRecommend,
            commentNotSatisfyCustSupport: commentNotSatisfiedCusSupport,
            continueUse: continueUseRating,
            custSupport: cusSupportRating,
            recommend: recommendRating,
        };
        const success = await saveSurvey({ surveyDocId, data, userId, userType, setSnackbarMessage, setSnackbarErrorMessage });
        if (success) setPageID(pageId + 1);

        logEvent(ANALYTICS_EVENT_SURVEY_SUBMITTED, { user_type: userType });
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider
            value={{
                ...context,
                scrollToPageTop,
                pageId,
                setPageID,
                totalSteps,
                userType,
                stepDirtyList,
                setStepDirtyList,
                surveyUser,
                // Emp Data
                empData,
                setEmpData,
                empHelpfulRating,
                setEmpHelpfulRating,
                empCommentMoreHelpful,
                setEmpCommentMoreHelpful,
                handleSubmitSurvey,
                continueUseRating,
                setContinueUseRating,
                commentNotContinueUse,
                setCommentNotContinueUse,
                commentMostValue,
                setCommentMostValue,
                commentImprove,
                setCommentImprove,
                recommendRating,
                setRecommendRating,
                commentNotRecommend,
                setCommentNotRecommend,
                cusSupportRating,
                setCusSupportRating,
                commentNotSatisfiedCusSupport,
                setCommentNotSatisfiedCusSupport,
                // Js Data
                jsData,
                setJsData,
                jsHelpfulRating,
                setJsHelpfulRating,
                jsCommentMoreHelpful,
                setJsCommentMoreHelpful,
                countryCode,
                setCountryCode,
                companyNameExists,
            }}
        >
            <AfitdPageHead title={PAGE_TITLE_SURVEY} />
            <Grid {...defns.pageContainer}>
                <Grid {...defns.pageTopNav}>
                    <TopNav isShowProgressBar={isLoadingSurvey} isBlank />
                </Grid>
                {!isLoadingSurvey && validSurveyCode ? (
                    <SimpleBar style={{ height: '100%', width: '100%', overflowX: 'hidden' }} ref={pageContentSimpleBarEl}>
                        <Container {...defns.container}>
                            <Grid {...defns.pageContent}>
                                <Grid {...defns.tempContainer}>
                                    {userType === USER_TYPE_EMP ? getEmpPageComponent({ pageId }) : getJSPageComponent({ pageId })}
                                    <br />
                                    <br />
                                    <br />
                                </Grid>{' '}
                            </Grid>
                        </Container>
                        <Footer />
                    </SimpleBar>
                ) : isLoadingSurvey ? null : surveyCompleted ? (
                    <PageMessage surveyCompleted />
                ) : (
                    <PageMessage surveyCodeInvalid />
                )}
            </Grid>
        </LayoutContext.Provider>
    );
};

export default SurveyLayout;
